import { useRef, useState } from "react";
import {
  PER_TIER_KYC_FIELD_KEYS,
  PER_TIER_KYC_METHOD,
  PER_TIER_KYC_MODE,
} from "./PerTierKyc.constants";
import {
  buildKycOverrideFromDraft,
  getInitialDraftForRecord,
  getRecordKycOverride,
  isCustomKycMode,
  isSameKycOverride,
  validateKycDraft,
} from "./PerTierKyc.utils";

export const usePerTierKyc = ({ onPersist, onNoChange } = {}) => {
  const [record, setRecord] = useState(null);
  const [draft, setDraft] = useState(() => getInitialDraftForRecord(null));
  const [errors, setErrors] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const customDraftStash = useRef(null);

  const openForRecord = (nextRecord) => {
    const nextDraft = getInitialDraftForRecord(nextRecord);
    setRecord(nextRecord);
    setDraft(nextDraft);
    setErrors({});
    setAcknowledged(false);
    setConfirming(false);
    setSaving(false);
    customDraftStash.current = null;
  };

  const closeAll = () => {
    setRecord(null);
    setConfirming(false);
    setAcknowledged(false);
    setSaving(false);
  };

  const updateDraftField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleModeChange = (mode) => {
    if (mode === PER_TIER_KYC_MODE.INHERIT) {
      setDraft((prev) => {
        if (isCustomKycMode(prev?.[PER_TIER_KYC_FIELD_KEYS.MODE])) {
          customDraftStash.current = prev;
        }
        return {
          ...getInitialDraftForRecord(null),
          [PER_TIER_KYC_FIELD_KEYS.MODE]: PER_TIER_KYC_MODE.INHERIT,
        };
      });
      setErrors({});
      return;
    }
    const stashed = customDraftStash.current;
    customDraftStash.current = null;
    if (stashed && isCustomKycMode(stashed[PER_TIER_KYC_FIELD_KEYS.MODE])) {
      setDraft(stashed);
    } else {
      setDraft({
        ...getInitialDraftForRecord(null),
        [PER_TIER_KYC_FIELD_KEYS.MODE]: PER_TIER_KYC_MODE.CUSTOM,
      });
    }
    setErrors({});
  };

  const handleMethodChange = (value) =>
    updateDraftField(PER_TIER_KYC_FIELD_KEYS.KYC_TYPE, value);

  const handleEnabledChange = (checked) =>
    updateDraftField(PER_TIER_KYC_FIELD_KEYS.ENABLED, Boolean(checked));

  const handlePlacementChange = (value) =>
    updateDraftField(PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT, value);

  const handleContractUrlChange = (value) =>
    updateDraftField(PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL, value);

  const handleWorkflowChange = (value) =>
    updateDraftField(PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW, value);

  const clearContractErrors = (prev) => {
    if (
      !prev[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] &&
      !prev[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]
    ) {
      return prev;
    }
    const next = { ...prev };
    delete next[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL];
    delete next[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL];
    return next;
  };

  const handleContractFile = (fileLocation, index) => {
    // Read the method from the previous draft (not the render closure) so a
    // method switch immediately before upload can't write into the wrong field.
    setDraft((prev) => {
      if (
        prev?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] === PER_TIER_KYC_METHOD.KRA
      ) {
        const docs = [
          ...(prev?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL] ?? []),
        ];
        docs[index ?? docs.length] = fileLocation;
        return { ...prev, [PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]: docs };
      }
      return {
        ...prev,
        [PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL]: fileLocation,
      };
    });
    // Contract errors are re-computed on save; clear both keys so a
    // method switch racing the upload can't leave a stale error behind.
    setErrors(clearContractErrors);
  };

  const handleContractRemove = (index) => {
    setDraft((prev) => {
      if (
        prev?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] === PER_TIER_KYC_METHOD.KRA
      ) {
        const docs = (
          prev?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL] ?? []
        ).filter((_, docIndex) => docIndex !== index);
        return { ...prev, [PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]: docs };
      }
      return { ...prev, [PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL]: "" };
    });
    setErrors(clearContractErrors);
  };

  const persist = async (override) => {
    if (!onPersist) return;
    setSaving(true);
    try {
      await onPersist({ record, override });
      closeAll();
    } finally {
      setSaving(false);
    }
  };

  const skipUnchanged = () => {
    if (onNoChange) {
      onNoChange();
      return;
    }
    closeAll();
  };

  const handleSave = () => {
    // No-op save (open + save without changes) must never rewrite the stored
    // override — just close.
    if (
      isSameKycOverride(
        getRecordKycOverride(record),
        buildKycOverrideFromDraft(draft)
      )
    ) {
      skipUnchanged();
      return;
    }
    if (!isCustomKycMode(draft?.[PER_TIER_KYC_FIELD_KEYS.MODE])) {
      persist(buildKycOverrideFromDraft(draft));
      return;
    }
    const validationErrors = validateKycDraft(draft);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    setAcknowledged(false);
    setConfirming(true);
  };

  const handleConfirm = () => {
    if (!acknowledged) return;
    const override = buildKycOverrideFromDraft(draft);
    if (isSameKycOverride(getRecordKycOverride(record), override)) {
      skipUnchanged();
      return;
    }
    persist(override);
  };

  return {
    record,
    draft,
    errors,
    acknowledged,
    confirming,
    saving,
    isOpen: Boolean(record),
    openForRecord,
    closeAll,
    handleModeChange,
    handleMethodChange,
    handleEnabledChange,
    handlePlacementChange,
    handleContractUrlChange,
    handleWorkflowChange,
    handleContractFile,
    handleContractRemove,
    handleSave,
    handleConfirm,
    setAcknowledged,
    backToEdit: () => setConfirming(false),
  };
};
